import { useMemo } from 'react';
import {
    useLocation,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import userViewContextResolvers from './resolvers';
import type { ChatUserViewContext, ContextResolverResult } from './types';

const useChatUserViewContext = (): ChatUserViewContext => {
    const { agentId = '' } = useParams();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const organizationId = agentId;

    return useMemo(() => {
        const initialResolvedContext: Required<ContextResolverResult> = {
            hints: [],
            ids: {},
            description: '',
            capabilities: [],
        };
        const resolvedContext = userViewContextResolvers.reduce((acc, resolver) => {
            if (!resolver.match({ pathname, searchParams })) {
                return acc;
            }
            const result = resolver.resolve({ pathname, searchParams });
            return {
                hints: [...acc.hints, ...(result.hints || [])],
                ids: { ...acc.ids, ...(result.ids || {}) },
                description: result.description || acc.description,
                capabilities: result.capabilities || acc.capabilities,
            };
        }, initialResolvedContext);

        return {
            agentId,
            section: 'system-agents',
            view: 'console-top-bar',
            ids: {
                organizationId,
                namespace: 'console',
                ...resolvedContext.ids,
            },
            description: resolvedContext.description || 'User is navigating the Voxity console web app.',
            capabilities: resolvedContext.capabilities,
            hints: resolvedContext.hints,
        };
    }, [agentId, pathname, searchParams, organizationId]);
};

export default useChatUserViewContext;
